import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { CLI_CLIENT_LABEL_CONSOLE, CLI_CLIENT_LABEL_DASHBOARD_ONBOARDING } from '../../../../../../../../../domain/cli-client-labels';
import { APIAccountSession } from '../../../../../../../../../typings/AccountSession.interface';
import Badge from '../../../../../common/base/Badge';

// The dashboard and the Console create CLI keys for themselves, stored under these labels.
// A Map, so a device named like an object property (e.g. "__proto__") isn't looked up as one.
const FRIENDLY_DEVICE_NAMES = new Map<string, string>([[CLI_CLIENT_LABEL_DASHBOARD_ONBOARDING, 'Dashboard login command'], [CLI_CLIENT_LABEL_CONSOLE, 'Hookdeck Console']]);
export const getDeviceName = (device_name: string): string => FRIENDLY_DEVICE_NAMES.get(device_name) ?? (device_name || 'Unnamed device');

// A browser is named by its system, like "macOS". A CLI is named by its device.
export const getSessionName = (session: APIAccountSession): string => session.client === 'cli' ? getDeviceName(session.device_name) : session.device_name;

// Shown on hover over a session's icon: the browser, like "Chrome on macOS", or "CLI".
export const getClientLabel = (session: APIAccountSession): string => {
  if (session.client === 'cli') {
    return 'CLI';
  }
  const is_system_known = session.device_name !== 'Unknown device';
  if (!session.browser) {
    return is_system_known ? `Browser on ${session.device_name}` : 'Browser';
  }
  return is_system_known ? `${session.browser} on ${session.device_name}` : session.browser;
};

// A CLI command shown inline in a sentence, as a small grey monospace badge.
export const CliCommand: React.FC<{
  children: string;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Badge mono={true} neutral={true}>{children}</Badge>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// A refresh that returns the same list doesn't re-render, so expiry is checked again this often.
const EXPIRY_CHECK_MS = 60_000;

/** The current time, kept up to date so a key or login shows as expired once it is. */
export const useNow = () => {
  const $ = _c(2);
  const [now, setNow] = useState(_temp);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const interval = setInterval(() => setNow(Date.now()), EXPIRY_CHECK_MS);
      return () => clearInterval(interval);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return now;
};
function _temp() {
  return Date.now();
}